import { useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import config from '../../config';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${config.API_URL}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });

      if (res.ok) {
        setSuccess(true);
      } else {
        const data = await res.json().catch(() => ({}));
        const detail = data?.detail;
        if (detail && typeof detail === 'object' && detail.code) {
          if (detail.code === 'RESET_PASSWORD_INVALID_PASSWORD') {
            setError(detail.reason || 'Please choose a different password.');
          } else {
            setError('Failed to reset password.');
          }
        } else if (detail === 'RESET_PASSWORD_BAD_TOKEN') {
          setError('This reset link is invalid or has expired. Please request a new one.');
        } else {
          setError(typeof detail === 'string' ? detail : 'Failed to reset password.');
        }
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <div className="flex min-h-screen bg-black text-white justify-center items-center p-4">
        <div className="bg-gray-800 p-8 rounded-lg w-full max-w-md border border-red-500 text-center space-y-4">
          <h2 className="text-2xl font-bold text-white">Invalid Link</h2>
          <p className="text-gray-400">This password reset link is missing its token.</p>
          <Link to="/forgot-password" className="text-blue-400 hover:text-blue-300">
            Request a new link
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-black text-white justify-center items-center p-4">
      <div className="bg-gray-800 p-8 rounded-lg w-full max-w-md border border-blue-500 space-y-4">
        <h2 className="text-2xl font-bold text-center text-white mb-2">Reset Password</h2>

        {success ? (
          <>
            <p className="text-gray-300 text-center">Your password has been reset.</p>
            <button
              onClick={() => navigate('/creator-login')}
              className="w-full bg-blue-500 hover:bg-blue-600 p-3 rounded-lg font-semibold transition"
            >
              Log In
            </button>
          </>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-red-900 border border-red-500 text-red-200 px-4 py-2 rounded">
                {error}
              </div>
            )}

            <input
              className="w-full p-2 bg-gray-900 border border-gray-600 rounded text-white placeholder-gray-400"
              type="password"
              placeholder="New password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />

            <input
              className="w-full p-2 bg-gray-900 border border-gray-600 rounded text-white placeholder-gray-400"
              type="password"
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />

            <button
              className="w-full bg-blue-500 text-white p-3 rounded-lg font-semibold hover:bg-blue-600 transition disabled:bg-gray-600"
              type="submit"
              disabled={submitting}
            >
              {submitting ? 'Resetting...' : 'Reset Password'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
